import PptxGenJS from "pptxgenjs";
import { loadCovers, addCoverSlides } from "./pptxCovers";

/**
 * PPTX executivo — um slide por indicador do período, com os mesmos dados
 * do modal de detalhes: KPIs (Meta, Realizado, % Meta–Realizado, Peso),
 * gráfico de evolução (Meta vs Realizado) e textos de Análise e Ações.
 *
 * @param {Object} p
 * @param {Array}  p.filteredData  indicadores do período (um slide por item)
 * @param {Array}  p.indicators    base completa (para a série histórica)
 * @param {string} p.periodLabel   rótulo do período (ex.: "jul. de 26")
 */
export async function generateIndicatorsPPTX({ filteredData, indicators, periodLabel, covers: coversOverride }) {
  const BORDO = "7A2433", GRAFITE = "2B2B30", AMARELO = "E8D833";
  const CINZA_LN = "E5E7EB", CINZA_TX = "475569", CINZA_MUT = "9CA3AF", BRANCO = "FFFFFF";

  // ---- helpers (réplica de utils/formatters) ----
  const formatValue = (value, unit) => {
    if (value === null || value === undefined || (value === "" )) return "N/A";
    const n = parseFloat(value);
    if (isNaN(n)) return "N/A";
    if (unit === "R$") return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
    if (unit === "%") return `${n.toFixed(1)}%`;
    if (unit === "Score") return `${n.toFixed(1)}`;
    return `${n.toFixed(1)} ${unit || ""}`.trim();
  };

  const perfPct = (actual, target, hib = true) => {
    if (actual == null || target == null) return null;
    if (Object.is(actual, -0)) actual = 0;
    if (Object.is(target, -0)) target = 0;
    if (target === 0 && !hib) { if (actual === 0) return 100; if (actual > 0) return 0; return null; }
    if (target === 0 && hib) { if (actual > 0) return 100; if (actual === 0) return 0; if (actual < 0) return -100; }
    if (hib) {
      if (actual === 0) return 0;
      if (actual > 0 && target > 0) return Math.round((actual / target) * 100);
      if (actual < 0 && target > 0) return Math.round(-(Math.abs(actual) / target) * 100);
      if (actual > 0 && target < 0) return Math.round(((Math.abs(actual) + Math.abs(target)) / Math.abs(target)) * 100);
      if (actual < 0 && target < 0) return Math.round((Math.abs(target) / Math.abs(actual)) * 100);
      return null;
    }
    if (actual === 0) return 100;
    if (actual !== 0) return Math.round((target / actual) * 100);
    return null;
  };

  const bandHex = (pct) => (pct == null ? CINZA_MUT : pct >= 100 ? "059669" : pct >= 80 ? "D97706" : "DC2626");

  const perspTheme = (name) => {
    const u = (name || "").toUpperCase();
    if (u.includes("FINANC")) return { main: "059669", dark: "065F46", tint: "ECFDF5" };
    if (u.includes("CLIENTE")) return { main: "2563EB", dark: "1E3A8A", tint: "EFF6FF" };
    if (u.includes("PROCESSO")) return { main: "7C3AED", dark: "5B21B6", tint: "F5F3FF" };
    if (u.includes("PESSOA")) return { main: "EA580C", dark: "9A3412", tint: "FFF7ED" };
    return { main: BORDO, dark: "5E1A26", tint: "FBEBEE" };
  };

  const hibOf = (v) => !(v === false || v === "F" || v === 0 || v === "0");
  const monthLabel = (d) => { try { return new Date(d).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }); } catch { return ""; } };
  const fullDate = (d) => { try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return ""; } };

  // ---- lista: 1 slide por indicador (dedup por nome, mantém o período mais recente) ----
  const byName = new Map();
  (filteredData || []).forEach((it) => {
    const k = it.indicator;
    const cur = byName.get(k);
    if (!cur || new Date(it.period) > new Date(cur.period)) byName.set(k, it);
  });
  const items = Array.from(byName.values()).sort((a, b) =>
    String(a.perspective).localeCompare(String(b.perspective)) || String(a.indicator).localeCompare(String(b.indicator))
  );

  const period = periodLabel || (items.length ? monthLabel(items[items.length - 1].period) : "");

  // ---- documento ----
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: "MARPLY", width: 13.333, height: 7.5 });
  pptx.layout = "MARPLY";
  pptx.author = "Marini BSC";
  pptx.company = "Marini Indústria de Compensados";
  pptx.title = `Indicadores — ${period}`;
  const PW = 13.333;
  const FOOTER = "Marini · Indicadores · Confidencial — uso interno da Diretoria";

  const addFooter = (s, n) => {
    s.addText(FOOTER, { x: 0.5, y: 7.1, w: 10.5, h: 0.3, fontFace: "Arial", fontSize: 8, color: CINZA_MUT, valign: "middle" });
    if (n) s.addText(String(n), { x: 12.4, y: 7.1, w: 0.5, h: 0.3, fontFace: "Arial", fontSize: 8, color: CINZA_MUT, align: "right", valign: "middle" });
  };

  // ---- capa + abertura (template Marini) ----
  const covers = await loadCovers("indicadores", coversOverride);
  const introCount = addCoverSlides(pptx, covers, PW, 7.5);

  // Capa textual padrão (fallback)
  if (introCount === 0) {
    const s = pptx.addSlide();
    s.background = { color: BORDO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.35, h: 7.5, fill: { color: AMARELO } });
    s.addShape(pptx.ShapeType.rect, { x: 0.35, y: 0, w: 0.12, h: 7.5, fill: { color: GRAFITE } });
    s.addText("INDICADORES", { x: 1.0, y: 2.4, w: 11.4, h: 1.0, fontFace: "Arial", fontSize: 46, bold: true, color: BRANCO });
    s.addText("Detalhamento por indicador", { x: 1.0, y: 3.35, w: 11.4, h: 0.5, fontFace: "Arial", fontSize: 20, color: BRANCO });
    s.addShape(pptx.ShapeType.rect, { x: 1.05, y: 3.95, w: 3.2, h: 0.06, fill: { color: AMARELO } });
    s.addText("Marini · Indústria de Compensados", { x: 1.0, y: 4.15, w: 11.4, h: 0.5, fontFace: "Arial", fontSize: 20, color: AMARELO });
    s.addText(
      [
        { text: "Período: ", options: { color: "E7CDD3" } },
        { text: period, options: { bold: true, color: BRANCO } },
        { text: `     ·     ${items.length} indicador(es)`, options: { color: "E7CDD3" } },
      ],
      { x: 1.0, y: 4.8, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 16 }
    );
    s.addText("Relatório executivo — Diretoria", { x: 1.0, y: 6.7, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 11, italic: true, color: "E7CDD3" });
  }

  const CX = 0.62, CW = 12.15;

  const kpiCard = (s, x, w, y, label, value, borderCol, fillCol, valCol) => {
    s.addShape(pptx.ShapeType.roundRect, { x, y, w, h: 1.15, rectRadius: 0.08, fill: { color: fillCol }, line: { color: borderCol, width: 1.25 } });
    s.addText(label, { x: x + 0.18, y: y + 0.14, w: w - 0.36, h: 0.3, fontFace: "Arial", fontSize: 10.5, bold: true, color: borderCol });
    s.addText(value, { x: x + 0.18, y: y + 0.44, w: w - 0.36, h: 0.6, fontFace: "Arial", fontSize: 21, bold: true, color: valCol, valign: "middle" });
  };

  const textBox = (s, x, y, w, h, title, titleCol, fillCol, body) => {
    s.addShape(pptx.ShapeType.roundRect, { x, y, w, h, rectRadius: 0.06, fill: { color: fillCol }, line: { color: titleCol, width: 0.75 } });
    s.addText(title, { x: x + 0.2, y: y + 0.12, w: w - 0.4, h: 0.3, fontFace: "Arial", fontSize: 12, bold: true, color: titleCol });
    s.addText(body && String(body).trim() ? String(body) : "—", {
      x: x + 0.2, y: y + 0.48, w: w - 0.4, h: h - 0.64, fontFace: "Arial", fontSize: 10.5, color: "334155",
      valign: "top", align: "left", lineSpacingMultiple: 0.95,
      fit: "shrink", // encolhe o texto p/ caber quando for muito longo (nunca corta)
    });
  };

  let pageNo = introCount + 1;
  items.forEach((it) => {
    const theme = perspTheme(it.perspective);
    const hib = hibOf(it.higher_is_better);
    const pct = perfPct(Number(it.actual), Number(it.target), hib);
    const s = pptx.addSlide();
    s.background = { color: BRANCO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.22, h: 7.5, fill: { color: theme.main } });

    // cabeçalho
    s.addText(it.indicator || "", { x: CX, y: 0.22, w: 9.0, h: 0.6, fontFace: "Arial", fontSize: 24, bold: true, color: theme.dark });
    s.addText(it.perspective || "", { x: CX + 0.02, y: 0.82, w: 9.0, h: 0.35, fontFace: "Arial", fontSize: 13, color: CINZA_MUT });
    s.addText(`Período: ${fullDate(it.period)}`, { x: PW - 4.6, y: 0.35, w: 4.0, h: 0.35, fontFace: "Arial", fontSize: 12, color: CINZA_TX, align: "right" });
    s.addShape(pptx.ShapeType.rect, { x: CX, y: 1.28, w: CW, h: 0.03, fill: { color: theme.main } });

    // KPIs
    const gap = 0.2, cw = (CW - gap * 3) / 4;
    let x = CX; const ky = 1.5;
    kpiCard(s, x, cw, ky, "META", formatValue(it.target, it.unit), "2563EB", "EFF6FF", "1E3A8A"); x += cw + gap;
    kpiCard(s, x, cw, ky, "REALIZADO", formatValue(it.actual, it.unit), "059669", "ECFDF5", "065F46"); x += cw + gap;
    kpiCard(s, x, cw, ky, "% META – REALIZADO", pct == null ? "N/D" : `${pct}%`, bandHex(pct), "F8FAFC", bandHex(pct)); x += cw + gap;
    kpiCard(s, x, cw, ky, "PESO", `${it.weight != null ? parseFloat(it.weight) : 1}`, "D97706", "FFFBEB", "92400E");

    // série histórica (mesma lógica do modal)
    const hist = (indicators || [])
      .filter((r) => r.indicator === it.indicator && new Date(r.period) <= new Date(it.period))
      .sort((a, b) => new Date(a.period) - new Date(b.period))
      .slice(-12)
      .map((r) => ({ label: monthLabel(r.period), realizado: Number(r.actual), meta: Number(r.target) }));

    // gráfico (esquerda)
    s.addText("Evolução Histórica (Meta vs Realizado)", { x: CX, y: 2.82, w: 7.4, h: 0.3, fontFace: "Arial", fontSize: 12, bold: true, color: "1F2937" });
    if (hist.length) {
      const labels = hist.map((h) => h.label);
      const chartData = [
        { name: "Realizado", labels, values: hist.map((h) => h.realizado) },
        { name: "Meta", labels, values: hist.map((h) => h.meta) },
      ];
      s.addChart(pptx.ChartType.line, chartData, {
        x: CX, y: 3.15, w: 7.4, h: 3.5,
        chartColors: ["22C55E", "3B82F6"],
        lineSize: 3, lineSmooth: true,
        lineDataSymbol: "circle", lineDataSymbolSize: 6,
        showLegend: true, legendPos: "t", legendFontSize: 11, legendColor: "334155",
        showTitle: false, showValue: false,
        catAxisLabelColor: "6B7280", catAxisLabelFontSize: 9,
        valAxisLabelColor: "9CA3AF", valAxisLabelFontSize: 9,
        valAxisMinVal: 0, valGridLine: { color: "EEF1F4" }, catGridLine: { style: "none" },
      });
    } else {
      s.addText("Sem histórico disponível.", { x: CX, y: 3.5, w: 7.4, h: 0.5, fontFace: "Arial", fontSize: 12, italic: true, color: CINZA_MUT });
    }

    // Análise + Ações (direita) — alturas proporcionais ao tamanho do texto
    const rx = CX + 7.7, rw = CW - 7.7;
    const rTop = 2.82, rBottom = 6.92, rGap = 0.16;
    const avail = rBottom - rTop - rGap;
    const la = (it.result_analysis || "").trim().length || 1;
    const lp = (it.proposed_actions || "").trim().length || 1;
    let hA = avail * (la / (la + lp));
    hA = Math.max(1.05, Math.min(avail - 1.35, hA)); // Análise: min 1.05; Ações fica com o restante (>= 1.35)
    const hP = avail - hA;
    textBox(s, rx, rTop, rw, hA, "Análise dos Resultados", "059669", "ECFDF5", it.result_analysis);
    textBox(s, rx, rTop + hA + rGap, rw, hP, "Ações Propostas", "D97706", "FFFBEB", it.proposed_actions);

    addFooter(s, pageNo++);
  });

  const stamp = new Date().toISOString().split("T")[0];
  const fileName = `indicadores-${(period || "periodo").replace(/[^\w-]+/g, "_")}-${stamp}.pptx`;
  await pptx.writeFile({ fileName });
  return fileName;
}
